import { useCallback, useEffect, useState } from 'react'

import { useQueryClient } from '@tanstack/react-query'

import { useBridge } from '@onl-web/shared/bridge'

import { refreshTokenFromBridge, registerTokenFetcher, setToken } from '@/apis/tokenStore'
import { getMockMode } from '@/lib/devMode'
import { setSentryUser } from '@/lib/sentry'
import { PAYMENT_KEYS } from '@/payment/query'

/**
 * payment webview 인증 초기화 (브릿지 핸들러 규약 v2).
 *
 * 흐름:
 *  - mock 모드: 더미 토큰으로 회원 모드 시뮬레이션
 *  - 브릿지 timeout: 토큰 없이 guest 모드로 진행 (화면은 떠야 하므로)
 *  - 실제 모드: bridge.getAuthToken 으로 accessToken 시도 → 성공이면 회원 /
 *    실패·timeout·빈 토큰이면 ready (디버그 페이지 등은 토큰 없이도 접근 가능)
 *  - bridge.getDeviceInfo 로 bottomPadding 을 --safe-bottom CSS 변수에 주입
 *    (상단 safe-area 는 Flutter 가 native inset 으로 직접 처리 → webview 미주입)
 *
 * 토큰 갱신:
 *  - tokenStore.registerTokenFetcher 로 브릿지 callHandler 래퍼 등록 → axios 401
 *    interceptor 가 refreshTokenFromBridge 호출해 자동 재시도 가능
 *  - visibilitychange (visible) 시 토큰 재요청 — Flutter 포그라운드 복귀 케이스
 *    (카카오 등 외부 redirect 후 복귀)
 *
 * 결제 진입 시점(sealed-vinyl 등)에서는 페이지 컴포넌트가 직접 토큰 유무를
 * 체크해서 분기한다. 이 훅은 토큰 초기화 시도 + safe-area 설정 + 갱신 트리거까지 담당.
 */

type AuthStatus = 'loading' | 'ready'
const BRIDGE_TIMEOUT_MS = 5000

const withTimeout = <T>(promise: Promise<T>, ms: number): Promise<T> =>
  Promise.race([promise, new Promise<never>((_, reject) => setTimeout(() => reject(new Error('timeout')), ms))])

export function useInitAuth() {
  const { isReady, isTimeout, callHandler } = useBridge()
  const queryClient = useQueryClient()
  const [authStatus, setAuthStatus] = useState<AuthStatus>('loading')

  /** 브릿지 callHandler 래퍼 — tokenStore 가 React 컨텍스트 없이 토큰 재요청 가능하도록 */
  const fetchTokenFromBridge = useCallback(async (): Promise<string | null> => {
    try {
      const { accessToken } = await withTimeout(callHandler('getAuthToken'), BRIDGE_TIMEOUT_MS)
      return accessToken ?? null
    } catch {
      return null
    }
  }, [callHandler])

  useEffect(() => {
    if (getMockMode()) {
      setToken('mock-token')
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setAuthStatus('ready')
      return
    }

    // 브릿지 연결 타임아웃 — 토큰 없이 guest 모드로 진행한다.
    if (isTimeout) {
      setAuthStatus('ready')
      return
    }

    if (!isReady) return

    let cancelled = false

    // 외부(axios 401 interceptor 등) 가 호출할 수 있도록 fetcher 등록
    registerTokenFetcher(fetchTokenFromBridge)

    // 초기 인증 — accessToken 만 사용. refreshToken 은 Phase 4 의 401 인터셉터용 (현재 미사용)
    withTimeout(callHandler('getAuthToken'), BRIDGE_TIMEOUT_MS)
      .then(({ accessToken }) => {
        if (cancelled) return
        if (accessToken) setToken(accessToken)
        setAuthStatus('ready')
      })
      .catch((error) => {
        // 토큰 못 받았어도 페이지는 계속 — 비회원 모드로 진행
        if (cancelled) return
        if (import.meta.env.DEV) {
          console.warn('[useInitAuth] getAuthToken failed → guest mode', error)
        }
        setAuthStatus('ready')
      })

    // 유저 식별 — getUserInfo.userId 로 Sentry user 설정. 실패·미제공이면 no-op(null).
    // ⚠ Flutter 가 payment webview 에 userId 를 채워주는지는 합의 진행 중(잠정) — 채워지면 자동 동작.
    withTimeout(callHandler('getUserInfo'), BRIDGE_TIMEOUT_MS)
      .then(({ userId }) => {
        if (cancelled) return
        setSentryUser(userId ?? null)
      })
      .catch(() => {
        // 유저 정보 미수신 — Sentry user 미설정(익명 이벤트로 진행)
      })

    // safe-area bottom — getDeviceInfo.bottomPadding. 실패해도 인증 흐름과 무관.
    // 상단(top) safe-area 는 Flutter 가 webview 컨테이너에 native inset 으로 직접 적용하므로
    // webview 에서 --safe-top 을 주입하면 이중 적용된다 (헤더가 그만큼 더 내려감) → 하단만 처리.
    withTimeout(callHandler('getDeviceInfo'), BRIDGE_TIMEOUT_MS)
      .then(({ bottomPadding }) => {
        if (cancelled) return
        if (bottomPadding != null) {
          document.documentElement.style.setProperty('--safe-bottom', `${bottomPadding}px`)
        }
      })
      .catch(() => {
        // 디바이스 정보 측정 실패 — --safe-bottom 미설정 (CSS fallback env(safe-area-inset-bottom))
      })

    return () => {
      cancelled = true
    }
  }, [isReady, isTimeout, callHandler, fetchTokenFromBridge])

  // Flutter 포그라운드 복귀(외부 redirect 후 카톡 → 우리 앱) 시:
  //  1) 토큰 재요청
  //  2) 결제수단 캐시 무효화 — 카카오페이 등록처럼 외부 redirect 후 백엔드 콜백으로
  //     서버 측 paymentMethodId 가 발급된 케이스에서 새 카드를 즉시 노출
  // visibility 가 visible 로 전환되는 순간만 트리거.
  useEffect(() => {
    if (getMockMode()) return

    const handleVisibility = () => {
      if (document.visibilityState !== 'visible') return
      void refreshTokenFromBridge()
      void queryClient.invalidateQueries({ queryKey: [PAYMENT_KEYS.PAYMENT_METHODS] })
    }
    document.addEventListener('visibilitychange', handleVisibility)
    return () => document.removeEventListener('visibilitychange', handleVisibility)
  }, [queryClient])

  return { authStatus }
}
